import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Modal } from 'react-bootstrap';
import api from "../../../api/axiosConfig";
import { useAuth } from '../../../context/AuthContext';
import './Media.css';

const Media = () => {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [captions, setCaptions] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, image: null });

  // Fetch all images for the current site
  useEffect(() => {
    fetchImages();
  }, [user?.site_id]);

  const fetchImages = async () => {
    try {
      const siteId = user?.site_id;
      if (!siteId) {
        setError('Site ID not found. Please contact support.');
        return;
      }

      setLoading(true);
      const response = await api.get(`/pictures?site_id=${siteId}`);
      
      if (response.data.success) {
        setImages(response.data.data || []);
      } else {
        setError('Failed to fetch images.');
      }
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Failed to load images.');
    } finally {
      setLoading(false);
    }
  };

  // Handle file selection for multiple images
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      // Validate file type
      if (!file.type.match('image/jpeg') && !file.type.match('image/png')) {
        errors.push(`${file.name}: Only JPG and PNG files are allowed`);
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        errors.push(`${file.name}: File size should be less than 2MB`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      
      // Initialize captions for new files
      const newCaptions = {};
      validFiles.forEach(file => {
        newCaptions[file.name] = '';
      });
      setCaptions(newCaptions);
      setError('');
    }
  };

  // Upload images to server
  const uploadImages = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one image to upload.');
      return;
    }

    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      const siteId = user?.site_id;
      if (!siteId) {
        setError('Site ID not found. Please contact support.');
        return;
      }

      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('picture_files', file);
        formData.append('caption', captions[file.name] || '');
        formData.append('site_id', siteId);
        // Note: post_id is not required for general media uploads

        const response = await api.post('/pictures', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(progress);
          },
        });

        if (!response.data.success) {
          throw new Error(response.data.message || `Failed to upload ${file.name}`);
        }

        return response.data.data;
      });

      await Promise.all(uploadPromises);
      
      setSuccessMessage('Images uploaded successfully!');
      setSelectedFiles([]);
      setCaptions({});
      setShowUploadModal(false);
      
      // Refresh the images list
      fetchImages();
      
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload images.');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  // Delete image
  const deleteImage = async (imageId) => {
    try {
      setLoading(true);
      const response = await api.delete(`/pictures/${imageId}`);
      
      if (response.data.success) {
        setSuccessMessage('Image deleted successfully!');
        setImages(prev => prev.filter(img => img.id !== imageId));
        setDeleteConfirm({ show: false, image: null });
      } else {
        setError('Failed to delete image.');
      }
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete image.');
    } finally {
      setLoading(false);
    }
  };

  // Handle caption change for new files
  const handleCaptionChange = (fileName, caption) => {
    setCaptions(prev => ({
      ...prev,
      [fileName]: caption
    }));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container className="media-container">
      <div className="media-header">
        <h1>Media Library</h1>
        <p>Manage images and media files for your website</p>
      </div>

      {error && <Alert variant="danger" className="media-alert">{error}</Alert>}
      {successMessage && <Alert variant="success" className="media-alert">{successMessage}</Alert>}

      {/* Upload Section */}
      <Card className="upload-section mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-1">Upload New Images</h5>
              <p className="text-muted mb-0">Supported formats: JPG, PNG • Max size: 2MB per file</p>
            </div>
            <Button 
              variant="primary" 
              onClick={() => setShowUploadModal(true)}
              disabled={loading}
            >
              Upload Images
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Images Grid */}
      {loading && images.length === 0 ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading images...</p>
        </div>
      ) : images.length === 0 ? (
        <Card className="text-center py-5">
          <Card.Body>
            <div className="empty-state">
              <i className="fas fa-images fa-3x text-muted mb-3"></i>
              <h5>No images found</h5>
              <p className="text-muted">Upload your first image to get started</p>
              <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                Upload Images
              </Button>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="images-header mb-3">
            <h5>All Images ({images.length})</h5>
          </div>
          <Row>
            {images.map(image => (
              <Col key={image.id} xs={6} md={4} lg={3} className="mb-4">
                <Card className="image-card">
                  <div className="image-container">
                    <Card.Img 
                      variant="top" 
                      src={image.picture_path} 
                      alt={image.caption || 'Website image'}
                    />
                    <div className="image-overlay">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setDeleteConfirm({ show: true, image })}
                        disabled={loading}
                      >
                        <i className="fas fa-trash"></i>
                      </Button>
                    </div>
                  </div>
                  <Card.Body>
                    <Card.Text className="image-caption">
                      {image.caption || 'No caption'}
                    </Card.Text>
                    <div className="image-meta">
                      <small className="text-muted">
                        {formatDate(image.created_at)}
                      </small>
                      <br />
                      <small className="text-muted">
                        {image.file_size ? formatFileSize(image.file_size) : 'Size unknown'}
                      </small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </>
      )}

      {/* Upload Modal */}
      <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Upload Images</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Select Images</Form.Label>
              <Form.Control
                type="file"
                accept="image/jpeg, image/png"
                onChange={handleFileSelect}
                multiple
              />
              <Form.Text className="text-muted">
                You can select multiple JPG or PNG files (max 2MB each)
              </Form.Text>
            </Form.Group>

            {selectedFiles.length > 0 && (
              <div className="selected-files">
                <h6>Selected Files ({selectedFiles.length})</h6>
                {selectedFiles.map(file => (
                  <Card key={file.name} className="mb-2">
                    <Card.Body className="py-2">
                      <Row className="align-items-center">
                        <Col xs={3}>
                          <img 
                            src={URL.createObjectURL(file)} 
                            alt="Preview" 
                            className="file-preview"
                          />
                        </Col>
                        <Col xs={6}>
                          <div className="file-info">
                            <strong>{file.name}</strong>
                            <br />
                            <small className="text-muted">
                              {formatFileSize(file.size)}
                            </small>
                          </div>
                        </Col>
                        <Col xs={3}>
                          <Form.Control
                            type="text"
                            placeholder="Caption"
                            value={captions[file.name] || ''}
                            onChange={(e) => handleCaptionChange(file.name, e.target.value)}
                            size="sm"
                          />
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="upload-progress mt-3">
                <div className="progress-info d-flex justify-content-between">
                  <small>Uploading...</small>
                  <small>{uploadProgress}%</small>
                </div>
                <div className="progress">
                  <div
                    className="progress-bar"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowUploadModal(false);
              setSelectedFiles([]);
              setCaptions({});
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={uploadImages}
            disabled={loading || selectedFiles.length === 0}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Uploading...
              </>
            ) : (
              `Upload ${selectedFiles.length} Image${selectedFiles.length !== 1 ? 's' : ''}`
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteConfirm.show} onHide={() => setDeleteConfirm({ show: false, image: null })}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this image? This action cannot be undone.
          {deleteConfirm.image?.caption && (
            <p className="mt-2">
              <strong>Caption:</strong> {deleteConfirm.image.caption}
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setDeleteConfirm({ show: false, image: null })}
          >
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={() => deleteImage(deleteConfirm.image.id)}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Deleting...
              </>
            ) : (
              'Delete Image'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Media;